import { Workflow, GitBranch, LayoutDashboard, Users } from "lucide-react";

const problems = [
    {
        num: "01",
        title: "Troppi software separati",
        text: "Rapportini da una parte, contratti su fogli di calcolo, preventivi in PDF, licenze su portali vendor, scadenze sparse e statistiche da ricostruire manualmente.",
    },
    {
        num: "02",
        title: "Processi manuali e poca tracciabilità",
        text: "Ogni passaggio richiede controlli, copia-incolla, verifiche e comunicazioni interne. Il rischio è perdere informazioni importanti su clienti, attività, contratti e vendite.",
    },
    {
        num: "03",
        title: "Il cliente non ha una vista unica",
        text: "Contratti, licenze, dispositivi, preventivi, servizi attivi e documenti sono spesso distribuiti tra email, allegati e portali diversi.",
    },
];

const solutionCards = [
    {
        icon: Workflow,
        title: "Operatività tecnica",
        text: "Interventi, rapportini, pianificazioni e storico cliente in un unico flusso, senza dispersione.",
    },
    {
        icon: GitBranch,
        title: "Contratti e ricavi ricorrenti",
        text: "Contratti prepagati, flat, rinnovi, consumi e marginalità sempre sotto controllo.",
    },
    {
        icon: LayoutDashboard,
        title: "CRM e tracciabilità commerciale",
        text: "Clienti, preventivi, opportunità e storico attività collegati alla scheda cliente.",
    },
    {
        icon: Users,
        title: "Portale cliente e trasparenza",
        text: "Un&apos;area dedicata dove il cliente consulta servizi, contratti, licenze e documenti.",
    },
];

export default function ProblemSolution() {
    return (
        <>
            {/* PROBLEM */}
            <section
                id="problema"
                data-testid="problem-section"
                className="relative bg-slate-50 py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">
                            <span className="h-px w-8 bg-cyan-500" />
                            Il problema
                        </span>
                        <h2
                            data-testid="problem-title"
                            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                        >
                            Quando gli strumenti non si parlano, il lavoro rallenta.
                        </h2>
                    </div>

                    <div
                        data-testid="problem-cards"
                        className="mt-14 grid gap-6 md:grid-cols-3"
                    >
                        {problems.map((p) => (
                            <div
                                key={p.num}
                                data-testid={`problem-card-${p.num}`}
                                className="reveal group relative rounded-2xl bg-white ring-1 ring-slate-200 p-8 hover:ring-slate-300 hover:shadow-lg transition"
                            >
                                <span className="font-display text-[64px] font-extrabold leading-none bg-gradient-to-br from-slate-200 to-slate-100 bg-clip-text text-transparent select-none">
                                    {p.num}
                                </span>
                                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                                    {p.text}
                                </p>
                                <div className="absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
                            </div>
                        ))}
                    </div>

                    <p
                        data-testid="problem-closing"
                        className="mt-14 max-w-3xl text-lg text-slate-700"
                    >
                        <span className="font-semibold text-slate-900">
                            TecSuite nasce per risolvere questo problema:
                        </span>{" "}
                        un&apos;unica piattaforma, costruita sui processi reali di un&apos;azienda IT.
                    </p>
                </div>
            </section>

            {/* SOLUTION */}
            <section
                id="soluzione"
                data-testid="solution-section"
                className="relative bg-white py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                            <span className="h-px w-8 bg-teal-500" />
                            La soluzione
                        </span>
                        <h2
                            data-testid="solution-title"
                            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                        >
                            Un&apos;unica suite per gestire operatività, contratti e relazione con il cliente.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                            TecSuite è il gestionale operativo pensato per aziende IT, MSP e system integrator che
                            vogliono avere controllo completo su attività tecniche, servizi ricorrenti, scadenze,
                            vendite e dati cliente.{" "}
                            <span className="text-slate-900 font-semibold">
                                Non sostituisce la contabilità
                            </span>
                            : mette ordine in tutto ciò che viene prima, durante e dopo il lavoro tecnico.
                        </p>
                    </div>

                    <div
                        data-testid="solution-cards"
                        className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
                    >
                        {solutionCards.map((c) => (
                            <div
                                key={c.title}
                                data-testid={`solution-card-${c.title}`}
                                className="reveal relative rounded-2xl bg-gradient-to-br from-slate-50 to-white ring-1 ring-slate-200 p-6 hover:ring-cyan-400/40 hover:shadow-md transition"
                            >
                                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-teal-400 text-white shadow-sm">
                                    <c.icon className="w-5 h-5" strokeWidth={2.25} />
                                </span>
                                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">
                                    {c.title}
                                </h3>
                                <p
                                    className="mt-2 text-[14.5px] leading-relaxed text-slate-600"
                                    dangerouslySetInnerHTML={{ __html: c.text }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
